/**
 * Microphone capture for server-side (Whisper) transcription.
 *
 * The same pipeline as Maildara's local speech feature: record with
 * MediaRecorder, decode the clip, resample to 16 kHz mono with an
 * OfflineAudioContext, and encode 16-bit PCM WAV. The server accepts exactly
 * that format, at most 90 seconds, and rejects silence before running the
 * engine — the silence gate is mirrored here so a quiet clip never leaves the
 * browser.
 */

export const VOICE_RATE = 16000
export const MAX_VOICE_SECONDS = 90
export const MAX_RECORDING_BYTES = 8 * 1024 * 1024
/** RMS of normalised samples below which a clip is treated as silence. */
export const SILENCE_RMS = 0.002

/** Encodes float samples as a canonical 44-byte-header 16 kHz mono PCM WAV. */
export function encodeVoiceWav(samples: ArrayLike<number>): ArrayBuffer {
  const length = Math.min(samples.length, VOICE_RATE * MAX_VOICE_SECONDS)
  const buffer = new ArrayBuffer(44 + length * 2)
  const view = new DataView(buffer)
  const ascii = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i))
  }
  ascii(0, 'RIFF')
  view.setUint32(4, buffer.byteLength - 8, true)
  ascii(8, 'WAVE')
  ascii(12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true)
  view.setUint16(22, 1, true)
  view.setUint32(24, VOICE_RATE, true)
  view.setUint32(28, VOICE_RATE * 2, true)
  view.setUint16(32, 2, true)
  view.setUint16(34, 16, true)
  ascii(36, 'data')
  view.setUint32(40, length * 2, true)
  for (let i = 0; i < length; i++) {
    const raw = samples[i]
    const value = Number.isFinite(raw) ? Math.max(-1, Math.min(1, raw as number)) : 0
    view.setInt16(44 + i * 2, Math.round(value * (value < 0 ? 32768 : 32767)), true)
  }
  return buffer
}

/** RMS of a WAV produced by encodeVoiceWav; the server's silence gate. */
export function wavRMS(wav: ArrayBuffer): number {
  const view = new DataView(wav)
  const count = (wav.byteLength - 44) / 2
  if (count <= 0) return 0
  let energy = 0
  for (let i = 44; i + 1 < wav.byteLength; i += 2) {
    const v = view.getInt16(i, true) / 32768
    energy += v * v
  }
  return Math.sqrt(energy / count)
}

/** Decodes a recorded clip and resamples it to the server's format. */
export async function recordingToWav(blob: Blob): Promise<ArrayBuffer> {
  const context = new AudioContext()
  try {
    const audio = await context.decodeAudioData(await blob.arrayBuffer())
    const frames = Math.min(Math.ceil(audio.duration * VOICE_RATE), MAX_VOICE_SECONDS * VOICE_RATE)
    const offline = new OfflineAudioContext(1, Math.max(frames, 1), VOICE_RATE)
    const source = offline.createBufferSource()
    source.buffer = audio
    source.connect(offline.destination)
    source.start()
    return encodeVoiceWav((await offline.startRendering()).getChannelData(0))
  } finally {
    await context.close()
  }
}

export type RecorderStatus = 'starting' | 'recording' | 'processing' | 'done' | 'cancelled' | 'failed'

export interface RecorderEvents {
  onStatus: (status: RecorderStatus) => void
  /** Elapsed whole seconds while recording. */
  onTick: (seconds: number) => void
  /** The finished clip, already converted. Not called after cancel. */
  onWav: (wav: ArrayBuffer) => void
  onError: (message: string) => void
}

export const RECORDER_ERRORS = {
  unsupported: 'Recording is unavailable in this browser. Try Chrome, Safari or Firefox, or continue typing.',
  denied: 'Microphone access was blocked. Allow the microphone in your browser’s site settings, then try again.',
  noDevice: 'No microphone is available. Connect a microphone and try again.',
  failed: 'Microphone recording failed. Please try again.',
  silent: 'No speech detected. Try speaking closer to your microphone.',
  tooShort: 'The recording was too short. Hold the microphone a moment longer.',
}

export function canRecord(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    Boolean(navigator.mediaDevices?.getUserMedia) &&
    typeof AudioContext !== 'undefined' &&
    typeof OfflineAudioContext !== 'undefined'
  )
}

function pickMimeType(): string | undefined {
  return ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'].find((type) =>
    MediaRecorder.isTypeSupported(type),
  )
}

/**
 * One recording. `start` asks for the microphone; `stop` ends the clip and
 * hands the WAV to `onWav`; `cancel` drops everything. Every browser event
 * after cancel or dispose is ignored.
 */
export function createRecording(events: RecorderEvents) {
  let active = false
  let recorder: MediaRecorder | null = null
  let stream: MediaStream | null = null
  let clock: ReturnType<typeof setInterval> | undefined
  let limit: ReturnType<typeof setTimeout> | undefined
  const chunks: Blob[] = []
  let bytes = 0

  function release() {
    clearInterval(clock)
    clearTimeout(limit)
    stream?.getTracks().forEach((track) => track.stop())
    stream = null
    recorder = null
  }

  function fail(message: string) {
    if (!active) return
    active = false
    release()
    events.onStatus('failed')
    events.onError(message)
  }

  async function start() {
    if (active) return
    active = true
    events.onStatus('starting')
    if (!canRecord()) {
      fail(RECORDER_ERRORS.unsupported)
      return
    }
    let media: MediaStream
    try {
      media = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
        video: false,
      })
    } catch (error) {
      const name = error instanceof DOMException ? error.name : ''
      fail(
        name === 'NotAllowedError' || name === 'SecurityError'
          ? RECORDER_ERRORS.denied
          : name === 'NotFoundError' || name === 'OverconstrainedError'
            ? RECORDER_ERRORS.noDevice
            : RECORDER_ERRORS.failed,
      )
      return
    }
    if (!active) {
      media.getTracks().forEach((track) => track.stop())
      return
    }
    stream = media
    const mimeType = pickMimeType()
    let rec: MediaRecorder
    try {
      rec = new MediaRecorder(media, mimeType ? { mimeType } : undefined)
    } catch {
      fail(RECORDER_ERRORS.unsupported)
      return
    }
    recorder = rec
    rec.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data)
        bytes += event.data.size
        if (bytes > MAX_RECORDING_BYTES && rec.state === 'recording') rec.stop()
      }
    }
    rec.onerror = () => fail(RECORDER_ERRORS.failed)
    rec.onstop = () => {
      release()
      if (!active) return
      events.onStatus('processing')
      void (async () => {
        try {
          const wav = await recordingToWav(new Blob(chunks, { type: rec.mimeType }))
          if (!active) return
          if (wav.byteLength < 44 + 1600) {
            fail(RECORDER_ERRORS.tooShort)
            return
          }
          if (wavRMS(wav) < SILENCE_RMS) {
            fail(RECORDER_ERRORS.silent)
            return
          }
          active = false
          events.onStatus('done')
          events.onWav(wav)
        } catch {
          fail(RECORDER_ERRORS.failed)
        }
      })()
    }
    rec.start(250)
    events.onStatus('recording')
    const started = Date.now()
    events.onTick(0)
    clock = setInterval(() => {
      events.onTick(Math.min(MAX_VOICE_SECONDS, Math.floor((Date.now() - started) / 1000)))
    }, 500)
    limit = setTimeout(stop, MAX_VOICE_SECONDS * 1000)
  }

  function stop() {
    if (!active) return
    clearInterval(clock)
    clearTimeout(limit)
    if (recorder && recorder.state === 'recording') {
      recorder.stop()
    } else if (!recorder) {
      // Still waiting for permission: stopping now is a cancel.
      cancel()
    }
  }

  function cancel() {
    if (!active) return
    active = false
    const rec = recorder
    release()
    try {
      if (rec && rec.state !== 'inactive') rec.stop()
    } catch {
      /* Already stopped. */
    }
    events.onStatus('cancelled')
  }

  return { start, stop, cancel, dispose: cancel, get active() { return active } }
}
